/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

import type {
  AccountInfo,
  InflationReward,
  ParsedAccountData,
  TransactionInstruction,
} from '@solana/web3.js'
import { useAnchorWallet, useWallet } from '@solana/wallet-adapter-vue'
import {
  PublicKey,
  StakeProgram,
} from '@solana/web3.js'
import { defineStore } from 'pinia'
import { useQuasar } from 'quasar'
import { computed, ref, watch } from 'vue'
import { withPriorityFees } from '~/features/priority-fee'
import { createStakePubkeyWithSeed, useMonitorTransaction, useStakeAccounts } from '~/hooks'
import { useConnectionStore, useEpochStore, useValidatorActiveStore } from '~/store'
import { addToDelegators, checkWalletInDelegators, getFilteredProgramAccounts, getStakesStatus, getWalletRewards, lamportsToSol, sendTransaction, sleep } from '~/utils'

export type EpochStakesRewards = {
  epoch: number
  stakeSum: number
  commission: number
  solPrice: number
}
export type ProgramAccount = {
  pubkey: PublicKey
  account: AccountInfo<ParsedAccountData>
  rewards: InflationReward[]
}

export type AccountFullInfo = {
  stakeAccount: ProgramAccount
  state: string
}

export const DEACTIVATE_ACTION = 'Deactivate'
export const ACTIVATE_ACTION = 'Activate'
export const WITHDRAW_ACTION = 'Withdraw'
export const MERGE_ACTION = 'Merge Accounts'

export const useStakeAccountStore = defineStore('stake-accounts', () => {
  const { monitorTransaction } = useMonitorTransaction()
  const { delegateAccount } = useStakeAccounts()
  const connectionStore = useConnectionStore()
  const wallet = useWallet()
  const anchorWallet = useAnchorWallet()
  const { notify } = useQuasar()

  const data = ref<ProgramAccount[]>([])
  const dataSelected = ref<string[]>([])
  const updatingRewards = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const retrieveDataReq = ref<boolean>(false)
  const stakesOnServer = ref<boolean>(false)
  const hasAllData = ref<boolean>(true)
  const checkInProgress = ref<boolean>(false)
  const stateLoaded = ref<boolean>(false)

  const epochStore = useEpochStore()
  const epoch = computed(() => epochStore.epochNumber)

  const validatorActiveStore = useValidatorActiveStore()
  const voterKey = computed(() => validatorActiveStore.voterKey)
  const network = computed(() => validatorActiveStore.network)
  const accountsByVote = computed(() => {
    if (voterKey.value) {
      return data.value.filter(
        acc =>
          acc.account.data?.parsed?.type !== 'delegated'
          || acc.account.data?.parsed?.info?.stake?.delegation?.voter === voterKey.value,
      )
    }
    return data.value
  })
  const loadingPubkeys = ref<Set<string>>(new Set())

  const totalStats = ref([

    {
      title: 'INACTIVE',
      color: '#647E82',
      value: 0,
    },
    {
      title: 'ACTIVE',
      color: '#00A5B9',
      value: 0,
    },
    {
      title: 'TOTAL',
      color: '#37A98C',
      value: 0,
    },
  ])

  const accountsFull = ref<AccountFullInfo[]>([])

  const checkStakesStatus = async () => {
    if (!checkInProgress.value) {
      // TODO: fix this endpoint on server
      return hasAllData.value = true
      checkInProgress.value = true
      hasAllData.value = await getStakesStatus(network.value, `${wallet.publicKey.value}`)
      checkInProgress.value = false
    }
  }

  setInterval(async () => {
    if (stakesOnServer.value && !retrieveDataReq.value) {
      await checkStakesStatus()
    }
  }, 60_000)

  function removeAccount(address: string) {
    data.value = data.value.filter(acc => acc.pubkey.toBase58() !== address)
  }

  const toggleSelection = (address: string, toggle = true) => {
    const index = dataSelected.value.indexOf(address)
    if (index !== -1) {
      dataSelected.value = [
        ...dataSelected.value.slice(0, index),
        ...dataSelected.value.slice(index + 1),
      ]
    } else if (toggle) {
      dataSelected.value.push(address)
    }
  }

  const toggleAll = () => {
    dataSelected.value = dataSelected.value.length === data.value.length ? [] : data.value.map(item => item.pubkey.toBase58())
  }

  const getInflationRewards = async (keys: PublicKey[], epoch: number): Promise<(InflationReward | null)[]> => {
    try {
      console.log('getInflationRewards epoch =', epoch)
      return await connectionStore.connection.getInflationReward(keys, epoch)
    } catch (error) {
      console.log('getInflationRewards error =', error)
      await sleep(500)
      return getInflationRewards(keys, epoch)
      // return keys.map((_key) => null);
    }
  }

  const updateRewards = async () => {
    const newAccountsFull = data.value.map((item) => {
      return {
        ...item,
      }
    })
    const startEpoch = epoch.value - 1
    if (startEpoch === -1) {
      return
    }
    updatingRewards.value = true

    stakesOnServer.value = (await checkWalletInDelegators(`${wallet.publicKey.value}`)).length > 0
    stateLoaded.value = true

    if (stakesOnServer.value) {
      const rewards = await getWalletRewards(network.value, `${wallet.publicKey.value}`)
      // console.log('rewards ============= ', rewards);
      for (const acc of newAccountsFull) {
        const accPubKey = acc.pubkey.toBase58()
        acc.rewards = rewards
          .filter((reward: { publicKey: string }) => reward.publicKey === accPubKey)
          .sort((a: { epoch: number }, b: { epoch: number }) => b.epoch - a.epoch)
        // console.log('acc ===========', acc);
      }
    } else {
      // const delegatedActivationEpochs = data.value
      //   .filter((acc) => acc.account.data?.parsed?.info.stake?.delegation.activationEpoch)
      //   .map((acc) => {
      //     const activationEpoch = acc.account.data?.parsed?.info.stake?.delegation.activationEpoch;
      //     return activationEpoch ? Number(activationEpoch) : startEpoch;
      //   }); // null coallescing not possible here
      for (const acc of newAccountsFull) {
        acc.rewards = []
      }

      const pubkeys = newAccountsFull.map(acc => acc.pubkey)
      for (let epoch = startEpoch; epoch > startEpoch - 5; epoch--) {
        const rewards = await getInflationRewards(pubkeys, epoch)
        // console.log(`getInflationRewards ${epoch} == `, rewards);
        rewards.forEach((reward: any, index: any) => {
          if (reward) {
            newAccountsFull[index]?.rewards.push(reward)
          }
        })
      }
    }
    for (const acc of newAccountsFull) {
      acc.rewards = acc.rewards.filter(
        reward =>
          reward.epoch >= (acc.account.data?.parsed?.info.stake?.delegation.activationEpoch ?? 0),
      )
    }
    // console.log('newAccountsFull ===========', newAccountsFull);
    data.value = newAccountsFull
    updatingRewards.value = false
  }

  setInterval(async () => {
    if (stakesOnServer.value && !hasAllData.value && !retrieveDataReq.value) {
      updateRewards()
      await checkStakesStatus()
    }
  }, 5000)

  const updateAccounts = async (pubkeys?: PublicKey[]) => {
    const newStats = [0, 0, 0, 0]
    const source = pubkeys ? accountsFull.value : accountsByVote.value
    const newData = pubkeys ? await loadStakeAccountsByPubkeys(pubkeys) : []

    accountsFull.value = source.map((item: any) => {
      // if used accountsFull then acc = item.stakeAccount
      const acc = pubkeys ? item.stakeAccount : item
      let state = ''
      try {
        const newDataIndex = pubkeys ? pubkeys.findIndex(pubkey => pubkey.equals(acc.pubkey)) : -1
        const stakeNewData = newDataIndex >= 0
          ? newData[newDataIndex]
          : acc.account

        if (!stakeNewData.state && epoch.value > 0) {
          const delegation = stakeNewData.data.parsed?.info.stake?.delegation
          if (delegation) {
            const activation = +delegation.activationEpoch
            const deactivation = +delegation.deactivationEpoch
            // state: 'active' | 'inactive' | 'activating' | 'deactivating';
            if (activation < epoch.value && deactivation > 99_999) {
              stakeNewData.state = 'active'
            } else if (activation === epoch.value && deactivation > 99_999) {
              stakeNewData.state = 'activating'
            } else if (activation < epoch.value && deactivation === epoch.value) {
              stakeNewData.state = 'deactivating'
            } else if ((activation < epoch.value && deactivation < epoch.value) || (activation === epoch.value && deactivation === epoch.value)) {
              stakeNewData.state = 'inactive'
            }
          }
        }
        state = stakeNewData.state
        if (acc.account.data?.parsed?.type === 'delegated') {
          if (state === 'active' || state === 'deactivating') {
            newStats[2] += lamportsToSol(acc.account.lamports)
          } else {
            newStats[1] += lamportsToSol(acc.account.lamports)
          }
        } else {
          newStats[0] += lamportsToSol(acc.account.lamports)
        }
      } catch (error) {
        console.log('getStakeActivation error ===', error)
      }
      return {
        stakeAccount: acc,
        state,
      }
    })

    newStats[3] = (newStats[0] ?? 0) + (newStats[1] ?? 0) + (newStats[2] ?? 0)
    for (const [index, item] of totalStats.value.entries()) {
      item.value = newStats[index] ?? 0
    }
  }

  setInterval(async () => {
    if (loading.value) {
      return
    }
    const accountsToUpdate: PublicKey[] = []
    for (const acc of accountsFull.value) {
      if (!acc.state && !loadingPubkeys.value.has(acc.stakeAccount.pubkey.toBase58())) {
        accountsToUpdate.push(acc.stakeAccount.pubkey)
      }
    }
    if (accountsToUpdate.length > 0) {
      updateAccounts(accountsToUpdate)
    }
  }, 10_000)

  async function loadStakeAccountsByPubkeys(pubkeys: PublicKey[]) {
    const data = await connectionStore.connection.getMultipleParsedAccounts(pubkeys)
    return data.value
  }

  async function load({ delay }: { delay?: number } = {}) {
    if (loading.value || !wallet.publicKey.value) {
      console.log('[Stake accounts] Skip loading...')
      return
    }
    console.log('[Stake accounts] Loading...')

    // stake user info account
    const filters = [
      {
        // 12 is Staker authority offset in stake account stake
        memcmp: {
          offset: 12 + 32,
          bytes: wallet.publicKey.value.toBase58(),
          // bytes: '2n5KTrz1b5MLkPATsosHJhc9JsRtnMxAEq1mWHZiHsZR',
        },
      },
      { dataSize: 200 },
      // {
      //   memcmp: {
      //     offset: 44,
      //     bytes: wallet.publicKey!.toBase58(),
      //   },
      // },
      // {
      //   dataSize: USER_STAKE_INFO_ACCOUNT_LAYOUT.span,
      // },
    ]

    loading.value = true

    if (delay) {
      await sleep(delay)
    }

    try {
      // @ts-expect-error ...
      data.value = await getFilteredProgramAccounts(
        connectionStore.connection,
        StakeProgram.programId,
        filters,
      )

      updateRewards()

      // console.log('[Stake accounts] Data:', data.value);
      // console.log(data.value.map((acc) => acc.pubkey.toBase58()).join('\n'));
    } catch {
      const additionalText = globalThis.location.pathname === '/' ? ' (for validator filtering)' : ''
      notify({
        message: `Failed to retrieve staking accounts${additionalText}. Please try again later, or select another RPC server and retry`,
        type: 'negative',
        position: 'bottom',
        multiLine: true,
        timeout: 0,
        textColor: 'black',
        actions: [
          {
            label: 'Retry',
            color: 'white',
            handler: load,
          },
          {
            label: 'Dismiss',
            color: 'white',
          },
        ],
      })
    } finally {
      loading.value = false
    }
  }

  watch(accountsByVote, () => updateAccounts(), { immediate: true })

  watch(
    wallet.publicKey,
    (newWallet, oldWallet) => {
      loading.value = false
      if (!newWallet || newWallet?.toBase58() !== oldWallet?.toBase58()) {
        stateLoaded.value = false
        data.value = []
      }
      load()
    },
    { immediate: true },
  )

  const calcStakeBalance = (accounts: ProgramAccount[]) => {
    const lamports = accounts.reduce((total, acc) => {
      total += acc.account.lamports
      return total
    }, 0)
    return lamportsToSol(lamports)
  }

  const mergeStakes = async () => {
    if (wallet.publicKey.value && dataSelected.value[0]) {
      const transactions: TransactionInstruction[] = []
      for (let i = dataSelected.value.length - 1; i > 0; i--) {
        const sourceStake = dataSelected.value[i]
        if (sourceStake) {
          const instructions = StakeProgram.merge({
            stakePubkey: new PublicKey(dataSelected.value[0]),
            sourceStakePubKey: new PublicKey(sourceStake),
            authorizedPubkey: wallet.publicKey.value,
          }).instructions

          transactions.push(...await withPriorityFees({ instructions }))
        }
      }

      await monitorTransaction(
        sendTransaction(connectionStore.connection, anchorWallet.value!, transactions, []),
        {
          commitment: 'finalized',
          onSuccess: async () => {
            await load()
          },
          onError: () => {},
        },
      )
    }
  }

  const deactivate = async (addresses: string[]) => {
    const transactions: TransactionInstruction[] = []
    const stakePubkeys: PublicKey[] = []

    for (const address of addresses) {
      loadingPubkeys.value.add(address)
      const stakePubkey = new PublicKey(address)
      stakePubkeys.push(stakePubkey)

      const instructions = StakeProgram.deactivate({
        stakePubkey,
        authorizedPubkey: wallet.publicKey.value!,
      }).instructions

      transactions.push(...await withPriorityFees({ instructions }))
    }

    await monitorTransaction(
      sendTransaction(connectionStore.connection, anchorWallet.value!, transactions, []),
      {
        commitment: 'finalized',
      },
    )

    await updateAccounts(stakePubkeys)

    for (const address of addresses) {
      loadingPubkeys.value.delete(address)
    }
  }

  const withdraw = async (stakes: { address: string, lamports?: number }[]) => {
    const transactions: TransactionInstruction[] = []
    // const stakePubkeys: PublicKey[] = []

    for (const stake of stakes) {
      loadingPubkeys.value.add(stake.address)
      const stakePubkey = new PublicKey(stake.address)
      let lamports = stake.lamports ?? 0
      if (!lamports) {
        const acc = data.value.find(item => item.pubkey.equals(stakePubkey))
        if (acc) {
          lamports = acc.account.lamports
        }
      }

      // stakePubkeys.push(stakePubkey)

      const instructions = StakeProgram.withdraw({
        stakePubkey,
        authorizedPubkey: wallet.publicKey.value!,
        toPubkey: wallet.publicKey.value!,
        lamports,
      }).instructions

      transactions.push(...await withPriorityFees({ instructions }))
    }

    await monitorTransaction(
      sendTransaction(connectionStore.connection, anchorWallet.value!, transactions, []),
      {
        commitment: 'finalized',
      },
    )
    for (const stake of stakes) {
      removeAccount(stake.address)
      loadingPubkeys.value.delete(stake.address)
    }
  }

  const activate = async (addresses: string[]) => {
    const stakePubkeys: PublicKey[] = []
    for (const address of addresses) {
      loadingPubkeys.value.add(address)
      const stakePubkey = new PublicKey(address)
      stakePubkeys.push(stakePubkey)
    }

    await delegateAccount(stakePubkeys)
    await updateAccounts(stakePubkeys)

    for (const address of addresses) {
      loadingPubkeys.value.delete(address)
    }
  }

  /**
   * return false if action is possible
   * else return message
   */
  const checkGroupAction = (actionType: string) => {
    const selectedAccounts = accountsFull.value.filter(acc =>
      dataSelected.value.includes(acc.stakeAccount.pubkey.toBase58()),
    )
    if (selectedAccounts.length < 2 && actionType === 'Merge accounts') {
      return 'You need to select at least two accounts'
    }
    if (selectedAccounts.length === 0) {
      return 'Please select at least one account'
    }
    const accTypes: string[] = []
    for (const acc of selectedAccounts) {
      if (actionType === ACTIVATE_ACTION && acc.state !== 'inactive') {
        return 'You can only activate an inactive account'
      }
      if (actionType === WITHDRAW_ACTION && acc.state !== 'inactive') {
        return 'You can only withdraw an inactive account'
      }
      if (actionType === DEACTIVATE_ACTION && acc.state !== 'active' && acc.state !== 'activating') {
        return 'You can only deactivate an active / activating account'
      }
      if (actionType === MERGE_ACTION) {
        if (acc.state === 'deactivating') {
          return 'You can merge two or more accounts if all of them are either active or inactive / activating'
        }
        accTypes.push(acc.state)
      }
    }
    if (actionType === MERGE_ACTION) {
      const hasActive = accTypes.includes('active')
      const hasInactive = accTypes.includes('inactive')
      const hasActivating = accTypes.includes('activating')
      if (hasActive && (hasActivating || hasInactive)) {
        return 'You can merge two or more accounts if all of them are either active or inactive / activating'
      }
    }
    return false
  }

  return {
    loading,
    stateLoaded,
    updatingRewards,
    data,
    dataSelected,
    totalStats,
    accountsFull,
    retriveDataReq: retrieveDataReq,
    loadingPubkeys,
    stakesOnServer,
    hasAllData,
    toggleSelection,
    toggleAll,
    load,
    stakeSolBalance: computed(() => calcStakeBalance(data.value)),
    removeAccount,
    runGroupActions: async (actionType: string) => {
      const wrongAction = checkGroupAction(actionType)
      if (wrongAction) {
        notify({
          message: `${wrongAction}`,
          type: 'warning',
          position: 'center',
          multiLine: true,
          timeout: 0,
          textColor: 'black',
          actions: [
            {
              label: 'Dismiss',
              color: 'white',
            },
          ],
        })
        return
      }
      if (actionType === ACTIVATE_ACTION) {
        await activate(dataSelected.value)
      }
      if (actionType === DEACTIVATE_ACTION) {
        await deactivate(dataSelected.value)
      }
      if (actionType === WITHDRAW_ACTION) {
        await withdraw(dataSelected.value.map(address => ({ address })))
      }
      if (actionType === MERGE_ACTION) {
        await mergeStakes()
      }
      dataSelected.value = []
    },
    activate,

    deactivate,

    withdraw,

    splitStake: async (address: string, lamports: number) => {
      loadingPubkeys.value.add(address)
      const stakeData = await createStakePubkeyWithSeed(wallet.publicKey.value!, data.value)
      const seed = stakeData.seed
      const splitStakePubkey = stakeData.stakePubkey

      await monitorTransaction(
        sendTransaction(
          connectionStore.connection,
          anchorWallet.value!,
          await withPriorityFees({
            instructions: StakeProgram.splitWithSeed({
              stakePubkey: new PublicKey(address),
              authorizedPubkey: wallet.publicKey.value!,
              basePubkey: wallet.publicKey.value!,
              splitStakePubkey,
              lamports,
              seed,
            }).instructions,
          }),
          [],
        ),
        {
          commitment: 'finalized',
        },
      )
      load()
      loadingPubkeys.value.delete(address)
    },

    addToDelegators: async () => {
      try {
        retrieveDataReq.value = true
        await addToDelegators(`${wallet.publicKey.value}`, network.value)
        stakesOnServer.value = true
      } finally {
        retrieveDataReq.value = false
      }
    },
  }
})
